import { MultiStepRoute } from "alicia-design-system/dist/esm/types";
import { BackgroundProps } from "features/shared/layout/plugins/background";
import { UseQueryParamsReturnType } from "hooks/useQueryParams";
import { AvailableLanguageType, BrandType } from "src/types";
import { TFunction } from "i18next";
import { FileState } from "alicia-design-system";
import { ComponentWithAs, IconProps } from "@chakra-ui/react";

export type MainQueryParams = UseQueryParamsReturnType & PartnerQueryType;

export type CheckinQueryParams = UseQueryParamsReturnType & {
  AVB_YEAR?: string;
  AVB_EVENT?: string;
  BAV_YEAR?: string;
  BAV_EVENT?: string;
  type?: CheckinTypeEnum;
  lang?: string;
};

export enum CheckinTypeEnum {
  SUPPLIER = "supplier",
  FREELANCER = "freelancer",
}

export type PartnerColors = {
  id: string;
  key: string;
  value: string;
};

export type PartnerCurrency = {
  id: string;
  currency_name: string;
  code: string;
};

export type PartnerDetailsType = {
  brand?: BrandType;
  colors?: PartnerColors[]; //https://aliciainsurance.atlassian.net/wiki/spaces/TECH/pages/295567361/Alicia+2.0+Colors
  languages: any;
  translations: any;
  default_language: AvailableLanguageType | null;
  available_languages: AvailableLanguageType[] | [];
  gtm: string;
  gtmId: string;
  hideKvk: boolean;
  allow_manual_company_information_input: boolean;
  redirectCalculator: boolean;
  name: string;
  manualCompanyInformationEnabled: boolean;
  validatePolicyStartDate: boolean;
  loaded: boolean;
  getPartner: () => Promise<void>;
  clearPartner: () => void;
  support_email: string;
  support_phone_number: string;
  trengo_key: string;
  is_direct: boolean;
  expo_ip?: string;
  checkinReturnURL?: string;
  setExpoIp: (expo_ip: string) => void;
  setCheckInReturnUrl: (returnUrl: string) => void;
  enable_visual_effects: boolean;
  hide_professions_on_welcome_screen: boolean;
  hide_profession_page: boolean;
  currency?: PartnerCurrency;
  updatePartnerColors: (colors: PartnerColors[]) => void;
};

/**
 * Auth types
 */

export type AuthDetailsType = {
  user?: UserType;
  authLoading: boolean;
  authenticated: boolean;
  access_token?: string;
  external_id?: string;
  refresh_token?: string;
  getUser: () => Promise<UserType>;
  setUserToken: (data: PartnerQueryType) => Promise<void>;
  refreshUserToken: () => Promise<void>;
  adminAuthenticate: (admin: AdminAuthCookieType) => Promise<void>;
  refreshAdminToken: () => Promise<void>;
  clearAuth: () => void;
  clearAdminAuth: () => void;
  putUserProfession: (data: ProfessionsResultType) => Promise<void>;
  manageUserAddress: (
    data: AddressType,
    type: UserAddressTypeEnum
  ) => Promise<void>;
  putUserCoc: (data: PutUserCocType) => Promise<void>;
  putUserPayment: (data: PutUserPaymentConcentType) => Promise<void>;
  putUserBirthDate: (data: PutUserDOBType) => Promise<void>;
  insuranceFlow: InsuranceFlow;
  setInsuranceFlow: (insuranceFlow: InsuranceFlow) => void;
  admin?: AdminAuthDetailsType;
  user_quote?: string;
  clearUserQuote: () => void;
  broker_quote?: string;
  clearBroker: () => void;
  requestUserAccountsList: (email: string) => Promise<UserType>;
};

export type UserAccountType = {
  name: string;
  front_end_domain: string;
};

export type AdminAuthCookieType = {
  token?: string;
  name?: string;
  returnURL?: string;
};

export type AdminAuthDetailsType = {
  access_token?: string;
  refresh_token?: string;
  name?: string;
  returnURL?: string;
};

export type BrokerCookieType = {
  quote?: string;
  name?: string;
  returnURL?: string;
};

export type PartnerQueryType = {
  external_id?: string;
  signature?: string;
  _gl?: string;
  signature_created_at?: string;
  is_broker_flow?: string;
  profession_id?: string;
  profession_name?: string;
  insurance_calculator?: string;
  expo_ip?: string;
  quote_id?: string;
  broker_quote?: string;
  funnel_type?: string;
  product_id?: string;
};

export type AuthResponseType = {
  access: string;
  refresh?: string;
};
export type BMIHistory = {
  bmi: number;
  weight: number;
  height: string;
};

/**
 * User types
 */

export type UserType = {
  company_name?: string;
  bmi_history?: BMIHistory[];
  chamber_of_commerce_number: string;
  chamber_of_commerce_number_verified: boolean;
  company_address?: AddressType;
  personal_address?: AddressType;
  date_of_birth?: string;
  email: string;
  entry_date?: string;
  first_name: string;
  id: string;
  last_name: string;
  phone_number?: string;
  policy_number?: string;
  profession?: string | ProfessionType;
  consent_share_data_with_partner: boolean;
  flagged: boolean;
  aov_date_of_birth?: boolean;
  iban?: string;
  iban_last4?: string;
  accounts?: UserAccountType[];
  profession_change_allowed?: boolean;
};

export type AddressType = {
  id?: string;
  city: string;
  country?: string;
  house_number: string;
  house_number_addition?: string;
  postal_code?: string;
  street: string;
  type?: UserAddressTypeEnum;
};

export enum UserAddressTypeEnum {
  COMPANY = "company",
  PERSONAL = "personal",
}

export type ProfessionType = {
  id: string;
  name: string;
};

export type ProfessionsResultType = {
  value: string;
  label?: string;
};

export type KvkResultType = {
  value: string;
  label: string;
  extra?: AddressType;
};

export type PutProfessionType = {
  profession: string;
};

export type PutCustomProfessionType = {
  first_name: string;
  email: string;
  custom_profession_description: string;
};

export type putFormalitiesType = {
  formalities_passed: boolean;
  formalities_failed_reason_description?: string;
};

export type PutUserCocType = {
  company_name: string;
  chamber_of_commerce_number: string;
  chamber_of_commerce_number_verified: boolean;
};

export type PutUserPaymentConcentType = {
  consent_share_data_with_partner: boolean;
  iban: string;
};

export type PutUserDOBType = {
  aov_date_of_birth: boolean;
  date_of_birth: string;
};

export type FormalitiesType = {
  products: any[];
  is_passed: boolean;
  failed_reason_description?: string;
};

/**
 * cart types
 */

export type CartItemInsuranceProductType = {
  id: string;
  name: string;
  description: string;
  price?: string;
  gtm_data?: any;
  net_price?: string;
  require_identity_verification?: boolean;
  type?: string | null;
  price_currency?: string;
  require_kvk?: boolean;
};

export type CartItemExtraCoverageType = {
  id: string;
  question: CartItemExtraCoverageQuestionType;
  option: CartItemExtraCoverageOptionType;
};

type CartItemExtraCoverageQuestionType = {
  id: string;
  text: string;
};

type CartItemExtraCoverageOptionType = {
  value: string;
  percentage_price_increase: string;
  amount_price_increase: string;
};

export type CartItemType = {
  id: string;
  insurance_product: CartItemInsuranceProductType;
  list_items: CartItemListItemType[];
  order_date: string;
  order_id: string;
  request_questions: boolean;
  inCheckoutCart?: boolean;
};

export type CartItemListItemType = {
  question_id: string;
  label: string;
  value: string;
  group: CartItemListItemGroupEnum;
  type: CartItemListItemTypeEnum;
};

export enum CartItemListItemTypeEnum {
  TEXT = "text",
  DATE = "date",
  MONEY = "money",
}

export enum CartItemListItemGroupEnum {
  EXTRA_COVERAGE = "extra_coverage",
  PRICE_INCREASE = "price_increase",
  TBP_PRICE = "tbp_price",
  CONSUMER_ELECTRONICS = "consumer_electronics",
}

export type PriceAnswerType = {
  id: string;
  date_value?: string | null;
  float_value?: string | null;
  money_value?: string | null;
  option?: PriceAnswerOptionType;
  question: PriceAnswerQuestionType;
};

export type PriceAnswerOptionType = {
  float_value?: string | number | null;
  id: string;
  int_value?: number;
  value?: string;
};

export type PriceAnswerQuestionType = {
  card_label?: string | null;
  group?: string;
  id: string;
  text: string;
};

export enum CheckoutFlowEnum {
  CAR = "car",
  CONSUMER = "consumer",
  AOV = "aov",
  NORMAL = "normal",
}

export type OnHoldItemsType = {
  [CheckoutFlowEnum.CAR]?: CartItemType[];
  [CheckoutFlowEnum.CONSUMER]?: CartItemType[];
  [CheckoutFlowEnum.AOV]?: CartItemType[];
  [CheckoutFlowEnum.NORMAL]?: CartItemType[];
};

export type CartType = {
  cartItemList: CartItemType[];
  checkoutCartItems: CartItemType[];
  checkoutFlow?: OnHoldItemsType;
  currentFlow?: CheckoutFlowEnum;
  entryDate?: string;
  showDisclaimerPage?: boolean;
  getCartItemList: () => Promise<CartItemType[]>;
  addToCart: (insurance_product: string) => Promise<void>;
  deleteFromCart: (cartItemId: string) => Promise<void>;
  prepareCheckoutFlow: () => Promise<void>;
  setCheckoutCartItems: () => Promise<void>;
  clearCartItems: () => Promise<void>;
  setEntryDate: (entryDate: string) => void;
  clearCheckoutFlow: () => void;
  clearCart: () => void;
};

export type TranslationLanguageType = {
  language: string;
  setLanguage: (data: string) => void;
  clearLanguage: () => void;
};

/**
 * product types
 */
export type ProductType = {
  description?: string;
  id: string;
  invoice_strategy?: string;
  is_price_increase?: string;
  card_label?: string;
  name?: string;
  net_price?: string;
  ordinal?: number;
  policy_terms_document?: string;
  ipid_document?: string;
  comparison_chart?: string;
  old_ipid_document?: string;
  pre_purchase_description?: string;
  price?: number;
  price_currency?: string;
  step_out_url?: string;
  user_step_out_url?: string;
  checked?: boolean;
  is_eligible?: boolean;
  type?: string | null;
  active_policies: PolicyType[];
  is_funnel_available: boolean;
  gtm_data?: any;
  drop_out_status:
    | ConsequenceEnum.HARD_DROP_OUT
    | ConsequenceEnum.DROP_OUT
    | null;
  claim_type?: ClaimType;
  require_kvk?: boolean;
  require_profession?: boolean;
  is_available_for_profession?: boolean;
};

export enum ConsequenceEnum {
  PRICE_INCREASE = "price_increase",
  NONE = "none",
  APPROVE = "approve",
  REJECT = "reject",
  DROP_OUT = "drop_out",
  HARD_DROP_OUT = "hard_drop_out",
}

export type ClaimType = {
  id: string;
  form: string;
  claim_type: string;
};

export type ProductListType = {
  products: ProductType[];
  isProductsAvailable: boolean;
  isPastPoliciesAvailable?: boolean;
  hasSingleProduct?: boolean;
  setProducts: (data: ProductType[]) => void;
  clearProducts: () => void;
  setIsPastPoliciesAvailable: (value: boolean) => void;
  clearProductsStore: () => void;
};

export type ProductPDP = {
  title: string;
  details: ProductPDPList[];
};

export type ProductPDPList = {
  id: string;
  question: string;
  answer: string;
  icon: string;
};
type PreviousDocuments = {
  created_date: string;
  url: string;
};

/**
 * policy types
 */
export type PolicyType = {
  id: string;
  premium: string;
  premium_untaxed: string;
  start: string;
  end: string;
  product: ProductType;
  active: boolean;
  risk_class: {
    id: string;
    name: string;
    product: string;
  };
  previous_policy_document_versions?: PreviousDocuments[];
  policy_number: string;
  confirmed_terms_of_conditions_at: string;
  policy_document: string;
  note: string;
  expired: boolean;
  self_cancel_max_end_date: string;
  self_cancel_allowed: boolean;
};

export type PoliciesType = {
  policies: PolicyType[];
  activePolicies: PolicyType[];
  inActivePolicies: PolicyType[];
  setPolicies: (data: PolicyType[]) => void;
  clearPolicies: () => void;
};

export type PoliciesCreationType = {
  id: string;
  start: string;
  product: string;
  price?: string;
  is_broker_flow?: boolean;
  quotation_id?: string;
};

export type PoliciesDataResponseType = {
  id: string;
  start: string;
  product_details: {
    id: string;
    name: string;
  };
  price?: string;
  is_broker_flow?: boolean;
  quotation_id?: string;
};

export type PoliciesCreationReasonType = {
  consequence: string;
  id: string;
  question_id: string;
  message?: string;
};

export type PoliciesCreationResponseType = {
  data: PoliciesDataResponseType;
  details: {
    non_field_errors: string[];
    reasons: PoliciesCreationReasonType[];
  };
  status: string;
};

export type ProductJourneyCreationType = {
  product: string;
};

export type ProductJourneyCreationDataType = {
  id: string;
  product_details: {
    id: string;
    name: string;
  };
  medical_underwriting_status: ProductJourneyEnum;
  redirect_url: string;
};

export enum ProductJourneyEnum {
  NONE = "none",
  MEDICAL_UNDERWRITING = "medical_underwriting",
}

export type ProductJourneyCreationResponseType = {
  data: ProductJourneyCreationDataType;
  details: {
    non_field_errors: string[];
    reasons: PoliciesCreationReasonType[];
  };
  status: string;
};

// companies types
export type CocAddressType = {
  city: string;
  country?: string;
  house_number?: string;
  house_number_addition?: string;
  postal_code?: string;
  street: string;
};

export type CocListResponseType = {
  id: string;
  name: string;
  address: CocAddressType;
};

export type CompanyType = {
  label: string;
  value: string;
  extra?: CocAddressType;
};

export type SearchProfessionType = {
  label: string;
  value: string;
};

// Form Inputs
export type IFormInput = {
  [id: string]: string | number;
};

export enum QuestionGroupEnum {
  NORMAL = "normal",
  PRICE = "price",
  QUIZ = "quiz",
  BMI = "bmi",
  EXTRA_COVERAGE = "extra_coverage",
  MEDICAL = "medical",
  CHECKIN_FLOW = "checkin_flow",
  MULTISELECT = "multiselect",
  CAR_PREMIUM = "car_premium",
  CAR_INSURANCE_COVERAGES = "car_insurance_coverages",
  ALLIANZ_LIABILITY_PREMIUM = "allianz_liability_premium",
  ALLIANZ_BUILDING_PREMIUM = "allianz_building_premium",
  ALLIANZ_CONTENT_PREMIUM = "allianz_content_premium",
  ALLIANZ_COVERAGES = "allianz_coverages",
}

export enum QuestionSystemNameEnum {
  // FE added
  AOV_PREMIUM_REVIEW = "aov_premium_review",
  AOV_PREMIUM = "aov_premium",
  CONSUMER_ELECTRONICS = "consumer_electronics",
  // AOV & ORV
  BIRTH_DATE = "birth_date",
  // ORV
  IS_SMOKING = "is_smoking",
  // AOV
  INCOME = "income",
  AOV_PERCENTAGE = "aov_percentage",
  AOV_WAITING_TIME = "aov_waiting_time",
  AOV_DURATION = "aov_duration",
  AOV_MENTAL_DISCOUNT = "aov_mental_discount",
  AOV_INDEXATION = "aov_indexation",
  // CHECKIN FLOW
  CHECKIN_AVB_YEAR = "avb_year",
  CHECKIN_BAV_YEAR = "bav_year",
  CHECKIN_AVB_EVENT = "avb_event",
  CHECKIN_BAV_EVENT = "bav_event",
  IMAGE_POLICY = "image_policy",
  // TYPES
  AMOUNT = "amount",
  TEXT = "text",
  BOOLEAN = "boolean",
  CHECK_IN_COVERAGE = "check_in_coverage",
  // EXTRA
  PRICE_SEARCH = "price_search",
  // DRONE
  DRONE_PRICE = "drone_purchase_price",
  DRONE_SERIAL_NUMBER = "drone_serial_number",
  DRONE_OPERATION = "drone_operation",
  // CONSUMER ELECTRONICS
  DEVICE_ID = "device_id",
  DEVICE_PRICE = "device_price",
  DEVICE_TYPE = "device_type",
  DEVICE_IMEI = "device_imei",
  DEVICE_SERIAL_NUMBER = "device_serial_number",
  COVERAGE_PER_CLAIM = "coverage_per_claim",
  COVERAGE_PER_YEAR = "coverage_per_year",
  // CAR PREMIUM
  ALLIANZ_HOUSE_NUMBER = "allianz_house_number",
  ALLIANZ_HOUSE_NUMBER_ADDITION = "allianz_house_number_addition",
  ALLIANZ_POSTCODE = "allianz_postcode",
  CAR_KM_PER_YEAR = "car_km_per_year",
  CAR_NO_OF_CLAIM_FREE_YEARS = "car_no_of_claim_free_years",
  CAR_VIN = "car_vin",
  CAR_COVERAGE = "car_coverage",
  CAR_DEDUCTIBLE = "car_deductible",
  CAR_LEGAL_ASSISTANCE_COVERAGE = "car_legal_assistance_coverage",
  CAR_ROAD_ASSISTANCE_COVERAGE = "car_road_assistance_coverage",
  CAR_PASSENGER_COVERAGE = "car_passenger_coverage",
  ALLIANZ_HOUSE_CONFORMITY = "allianz_house_conformity",
  ALLIANZ_HOME_OWNERSHIP = "allianz_home_ownership",
  ALLIANZ_FAMILY_COMPOSITION = "allianz_family_composition",
  // CONSUMER INSURANCE
  ALLIANZ_DEDUCTIBLE = "allianz_deductible",
  ALLIANZ_ALL_RISK = "allianz_all_risk",
  ALLIANZ_GLASS_COVERAGE = "allianz_glass_coverage",
}

export enum AddressLookupEnum {
  STREET = "street",
  CITY = "city",
  MUNICIPALITY = "municipality",
  PROVINCE = "province",
}

export type QuestionType = {
  id: string;
  group: QuestionGroupEnum;
  ordinal?: string;
  placeholder?: string;
  product: string;
  product_name?: string;
  price_currency?: string;
  text?: string;
  subtext?: string;
  extra_info?: string;
  system_name?: QuestionSystemNameEnum;
  options: QuestionOptionType[];
  user_answer?: UserAnswerType[];
  card_label?: string;
  label?: string;
  additional_info?: string;
  depends_on_options?: DependsOnOptionsType[];
};

export type DependsOnOptionsType = {
  id: string;
  question: string;
};

export type TextQuestionType = {
  id: string;
  group: QuestionGroupEnum;
  ordinal?: string;
  placeholder?: string;
  product: string;
  product_name?: string;
  price_currency?: string;
  subtext?: string;
  system_name?: QuestionSystemNameEnum;
  text?: string;
  user_answer?: UserAnswerType[];
  card_label?: string;
};

export type UserAnswerType = {
  id: string;
  option: string;
  additional_detail?: UserAnswerAdditionalDetails;
  date_value: string | null;
  float_value: string | number | null;
  money_value: string | null;
  multi_select_options: string[] | null;
  text_value: string | null;
};

export type UserAnswerAdditionalDetails = {
  id: string;
  reason: string;
};

export type ProductQuestionType = {
  questions?: QuestionType[];
};

export type ExtraCoverageType = {
  [product: string]: QuestionType[];
};

export type QuestionOptionType = {
  consequence?: string;
  default?: boolean;
  id: string;
  ordinal?: string;
  value: string;
  label?: string;
  allow_additional_details?: boolean;
  additional_detail_title?: string;
  additional_detail_description?: string;
  amount_price_increase?: string;
  multi_selectable?: boolean;
  int_value?: number;
  dependency_options?: {
    [id: string]: {
      percentage_price_increase: number;
      amount_price_increase: number;
    };
  };
  details?: {
    description?: string;
    features?: string[];
  };
};

export type FormattedQuestionsType = {
  questions?: QuestionType[];
  sortedRoutes?: SortedRoutesType[];
  extraCoverages?: ExtraCoverageType;
  currentProduct?: string;
  currentQuestionIndex: number;
  latestQuestionIndex: number;
  nextRoute: string;
  animate: string; //animation for custom layouts
  hidePrevious: boolean;
  setAnimate: (value: any) => void;
  setHidePrevious: (value: boolean) => void;
  getQuestions: (
    productIdList: string[],
    isCalculator?: boolean
  ) => Promise<void>;
  setNextRoute: (route: string) => void;
  setSortedRoutes: (routes: SortedRoutesType[]) => void;
  setCurrentQuestionIndex: (index: number, latestindex: number) => void;
  clearQuestions: () => void;
  formalities: string[];
  formalitiesChecked: (item: string) => void;
  setAovPresetPrices: (productId: string) => Promise<void>;
  presets?: AOVPresetPriceType;
};

export type ConsumerExtraInformationType = {
  coverages?: string[];
  description?: string;
  extra_content?: string;
};

export type RejectedReasonType = {
  error_code: string;
  error_message: string;
};

export type CrossSellProductType = {
  [key: string]: {
    id: string;
    type: ProductTypeEnum;
    name: string;
    extra_information?: ConsumerExtraInformationType;
    price: number;
    all_risk_price?: number;
    glass_coverage_price?: number;
    rejected_reason?: RejectedReasonType;
  };
};

export type ConsumerDetailedQuoteType = {
  id: string;
  type: ProductTypeEnum;
  name: string;
  extra_information?: ConsumerExtraInformationType;
  price: number;
  all_risk_price?: number;
  glass_coverage_price?: number;
  cross_sell_products?: CrossSellProductType;
  rejected_reason?: RejectedReasonType;
};

export type CarClauseType = {
  number: string;
  description: string;
  text: number;
  version: number;
};

export type CarExtraCoveragePriceType = {
  [id: string]: number;
};

export type ConsumerType = {
  consumerCoveragePrices?: {
    car_product?: {
      [id: string]: number | CarExtraCoveragePriceType;
    };
    consumer_product?: ConsumerDetailedQuoteType;
  };
  consumerCoverageError: boolean;
  consumerCoverageErrorCode?: string;
  consumerCoverageClause?: CarClauseType;
  setConsumerCoveragePrices: (productId: string) => Promise<void>;
  clearConsumer: () => void;
};

export type AOVPresetPriceType = {
  [title: string]: {
    net_price: string;
    gross_price: string;
  };
};

export type AovPresetCardProps = {
  keyName: string;
  title: string;
  type: string;
  mostChosen: boolean;
  description: string;
  choices: {
    title: string;
    label: string;
    group: string;
    system_name: QuestionSystemNameEnum;
    question: string;
    option: string;
  }[];
  price: {
    net_price: string;
    gross_price: string;
  };
  icon: ComponentWithAs<"svg", IconProps>;
  priceLoading: boolean;
};

export type SortedRoutesType = Omit<MultiStepRoutesType, "component">;
export type MultiStepRoutesType = {
  name: string;
  path: string;
  component: React.FC;
  type?: MultiStepRouteType;
  customTitle?: string;
  product?: string;
  isSkipTitle?: boolean;
  system_name?: QuestionSystemNameEnum;
  group?: string;
  question?: string;
  questions?: QuestionType[];
  isCheckIn?: boolean;
};

export enum MultiStepRouteType {
  PERSONAL = 0,
  ADDITIONAL = 1,
  PAYMENT = 2,
  CLAIM = 3,
  EXIT_SURVEY = 4,
  INTRO = 5,
  CUSTOMER_EFFORT_SCORE = 6,
}

export type FileUploadedType = {
  id: string;
  file: string;
  name: string;
};

// Answer types
export type AnswersType = {
  answers: AnswerType[];
  setAnswer: (
    questionId: string,
    optionId: string,
    isCheckinFlow?: boolean
  ) => Promise<void>;
  setAnswerForIncomeOrBirthDate: (
    questionId: string,
    value: string,
    type: "birthDate" | "income" | "suggestion" | "price"
  ) => Promise<void>;
  setAnswerForBMI: (questionId: string, height: number, weight: number) => void;
  setAnswerForFloat: (
    questionId: string,
    value: string,
    type?: "freelancer" | "supplier"
  ) => Promise<void>;
  setAnswerForText: (questionId: string, value: string) => Promise<void>;
  setAnswerForDevice: (questionId: string, deviceName: string) => Promise<void>;
  setAnswerForDocumentUpload: (
    questionId: string,
    files: FileState[]
  ) => Promise<void>;
  setAnswerForMultiSelect: (
    questionId: string,
    value: string[]
  ) => Promise<void>;
  setConsumerAnswer: (premiumAnswers: ConsumerAnswerType) => Promise<void>;
  clearAnswers: () => void;
};

export type AnswerType = {
  question: string;
  id?: string;
  option?: string | BMIRequestType;
  date_value?: string;
  money_value?: string;
  float_value?: number;
  text_value?: string;
  files?: FileState[] | string[];
  multi_select_options?: string[];
};

export type ConsumerAnswerType = {
  group: QuestionGroupEnum;
  answers: {
    id?: string;
    question?: string;
    text_value?: string;
    float_value?: string;
    option?: string;
    date_value?: string;
  }[];
};

export type FileReturnType = {
  id: string;
  file: string;
  name: string;
};

export type PreviousAnswerResponseType = {
  id: string;
  date_value?: string;
  money_value?: string;
  float_value?: number;
  text_value?: string;
  files?: FileReturnType[];
  additional_detail?: UserAnswerAdditionalDetails;
  option?: QuestionOptionType | string;
  override?: string;
  question: string;
};

export enum StepTypeEnum {
  DEFAULT = "default",
  CALCULATOR = "calculator",
  AOV = "aov",
  AOV_ACTURE = "aov_acture",
  CONSUMER_INSURANCE = "consumer_insurance",
}

export type StepsType = {
  steps: HeaderStepType[];
  currentId: string;
  isPageLoading?: boolean;
  setSteps: (data: StepTypeEnum, t: TFunction) => void;
  setCurrentStepId: (id: string) => void;
  setIsPageLoading: (value: boolean) => void;
  clearSteps: () => void;
};

export type HeaderStepType = {
  id: string;
  title: string;
  widthPercentage: string;
  mobileWidthPercentage: string;
};

// User choices types
export type UserChoicesType = {
  userChoices: UserChoiceType[];
  setUserChoice: (data: UserChoiceType) => void;
  clearChoices: () => void;
};

export type UserChoiceType = {
  systemName: string;
  choice?: number | string | string[];
  customData?: string;
  questionId?: string;
};

// BMI types
export type BMIRequestType = {
  height: number;
  weight: number;
  product?: string;
};

export type BMIResponseType = {
  answer: { id: string; consequence: string };
  bmi: number;
  created: string;
  height: string;
  id: string;
  user: string;
  weight: number;
  failed: boolean;
};

//refund types
export type RefundType = {
  amount: number;
  currency: string;
};

//cancel insurance survey type
export type CancelInsuranceSurveyType = {
  policyId: string;
  routes: MultiStepRoute[];
  currentRouteIndex: number;
  nextRoute: string;
  latestRouteIndex: number;
  setRoutes: (data: any) => void;
  setCurrentRouteIndex: (index: number, latestRouteIndex: number) => void;
  setNextRoute: (route: string) => void;
  setPolicyId: (policyId: string) => void;
};

export type SurveyAnswersType = {
  question_id: number;
  choice: string;
  comment: null | string;
  question?: {
    id?: number;
  };
};

export type SimpleSatDataType = {
  answers: any[];
  ticket: SimpleSatTicketType;
};

export type SimpleSatTicketType = {
  id?: number;
  external_id?: string;
  subject?: string;
};

export type SimpleSatStateType = {
  data: SimpleSatDataType;
  loading: boolean;
  createResponse: (
    surveyId: number,
    questionId: number,
    data: any,
    customData?: { fieldType: string; fieldValue: string }
  ) => Promise<boolean>;
  clear: () => void;
};

// survey props types
export type SurveyPropsType = {
  surveyId: number;
  surveyType?: string;
  typeId?: string;
  surveyQuestionId: number;
  previousQuestionId?: number;
  onSkip?: () => void;
  onNext: () => void;
  title?: string;
  titleDescription?: string;
  subText?: string;
  subTextDescription?: string;
  inputTitle?: string;
  inputPlaceholder?: string;
  isPreviousChoiceShown?: boolean;
};

export type SurveyRoutesType = {
  name: string;
  path: string;
  component: React.FC<any>;
  surveyId: number | string;
  surveyQuestionId?: number | string;
  previousQuestionId?: number;
  navigateRoute?: string;
  title?: string;
  titleDescription?: string;
  subText?: string;
  subTextDescription?: string;
  inputTitle?: string;
  inputPlaceholder?: string;
  isPreviousChoiceShown?: boolean;
};

export type SurveyType = {
  currentRouteIndex: number;
  latestRouteIndex: number;
  setCurrentRouteIndex: (value: number, latestRouteIndex: number) => void;
  clearSurvey: () => void;
};

// support types
export enum SupportActionEnum {
  CALL = "call",
  MAIL = "mail",
  CHAT = "chat",
  FAQ = "faq",
  WHATSAPP = "whatsapp",
  ALICIA_AI = "alicia-ai",
}

export type TitleType = {
  title: string;
  setTitle: (title: string) => void;
  clearTitleStore: () => void;
};

/**
 * Contact request types
 */

export type ContactRequestType = {
  first_name: string;
  last_name?: string;
  phone_number: string;
  email: string;
  policy_number?: string;
  intent: string;
};

/**
 * Product group types
 */
export enum ProductGroup {
  INTERNAL = "internal",
  EXTERNAL = "external",
}

// error page props types
export type ErrorPagePropsType = {
  title?: string;
  description?: string;
  buttonText?: string;
  backdrop?: BackgroundProps;
  navigateTo?: string;
};

export type CheckInType = {
  avbEvent?: string;
  bavEvent?: string;
  avbYear?: string;
  bavYear?: string;
  checkInType?: CheckinTypeEnum;
  checkInContent?: {
    bavTitle?: string;
    avbTitle?: string;
  };
  setCheckInData: (data: {
    avbEvent?: string;
    bavEvent?: string;
    avbYear?: string;
    bavYear?: string;
    checkInType?: string;
  }) => void;
  setCheckInContent: (data: { bavTitle?: string; avbTitle?: string }) => void;
  clearCheckInData: () => Promise<void>;
};

export enum InsuranceFlow {
  DEFAULT = "default",
  CALCULATOR = "calculator",
  CHECKIN = "checkin",
}

export type NavigationRouteType = {
  id: string;
  label: string;
  route?: string;
  icon?: ComponentWithAs<"svg", IconProps>;
  subMenu?: NavigationSubRouteType[];
  count?: number;
};

export type NavigationSubRouteType = {
  id: string;
  label: string;
  route?: string;
  icon?: ComponentWithAs<"svg", IconProps>;
};

//server-error-codes
// Should add the rest of error types
export type ErrorType =
  | "login"
  | "other"
  | "email-exists"
  | "postal_code-invalid";

export type CustomErrorCodeResponseType = {
  code: number;
  message: string;
};

export type DeviceType = {
  label: string;
  value: string;
  extra?: DeviceDetailsType;
};

export type DeviceDetailsType = {
  manufacturer: string;
  price?: string;
};

export enum QuestionMultiSelectTypeEnum {
  ALL = "all",
  NONE = "none",
  NOT_SELECTABLE = "not-selectable",
}

export type RoutesType = {
  name: string;
  path: string;
  component: React.FC<any>;
  type: MultiStepRouteType;
  customTitle: string;
  isSkipTitle?: boolean;
  system_name?: QuestionSystemNameEnum;
  product?: string;
  group?: string;
  question?: string;
  questions?: QuestionType[];
};

export type OptionListType = {
  value: string;
  label: string;
};

// invoice types
export type InvoiceType = {
  document: string;
  id: string;
  invoice_date: string;
  invoice_number: string;
  status: string;
  total: string;
};

export type SelectValueType = {
  label: string;
  value: string;
  infoText?: string;
  isDisabled?: boolean;
  disableInfoText?: string;
};

export enum FunnelFlowTypeEnum {
  NORMAL = "normal",
  BROKER = "broker",
}

export type ProductEligibilityCheckType = {
  user: string;
  products: string[];
  funnel_flow: FunnelFlowTypeEnum;
  start_date?: string;
};

export type ProductEligibilityResultType = {
  key?: string;
  id: string;
  name: string;
  internal_name: string;
  type: string;
  is_eligible: boolean;
  active: boolean;
  price: number;
  price_currency: string;
  answers: {
    question: string;
    value?: string;
    int_value?: number;
    money_value?: string;
    date_value?: string;
    overridden?: boolean;
  }[];
  extra_details: ProductEligibilityExtraDetailsType[];
};

export type ProductEligibilityExtraDetailsType = {
  title: string;
  value: string;
  type: "text" | "url";
  url?: string;
};

export type QuoteRequestType = {
  status: string;
  products: string[];
};

export enum ProductTypeEnum {
  AOV = "aov",
  AOV_ACTURE = "aov_acture",
  CAR_INSURANCE = "car",
  ALLIANZ_LIABILITY = "allianz_liability",
  ALLIANZ_BUILDING = "allianz_building",
  ALLIANZ_CONTENT = "allianz_content",
}

// feature flags types

export type FeatureFlagData = {
  active: boolean;
  include: string[];
  exclude: string[];
  extra?: ExtraData;
};

export type ExtraData = {
  support_times: SupportedTimesEnum;
};

export enum SupportedTimesEnum {
  OFFICE_HOURS = "office-hours",
  OUTSIDE_OFFICE_HOURS = "outside-office-hours",
  ALWAYS = "always",
}

export type ABTestData = {
  active: boolean;
  isTestVariant: boolean;
};

export type FeatureFlagStatus = {
  active: boolean;
  extra?: any;
};

export type Environments = "development" | "qa" | "stage" | "production";

export type FeatureFlag = Record<Environments, FeatureFlagData>;
